// ============================================================================
// Canonical Cattle forecast-batch detail route contract
// ============================================================================
//
// CattleBatchesRouter accepts every /cattle/batches/forecast/* suffix and
// CattleForecastBatchPage intentionally owns its invalid-month state. The
// released capability therefore admits one decoded-safe raw suffix, including
// a non-month value such as `not-a-month`, so the page can render that explicit
// state. A percent-encoded suffix is NOT normalized here, because the renderer
// receives the raw pathname suffix and would reject it.
//
// Search and hash are intentionally excluded.  This projection has no
// location-state contract beyond its month key, so accepting either would
// create an unreviewed second direct-entry shape. Rejected variants receive
// explicit route recovery, and the browser receipt proves their Back/Forward
// fence.
// ============================================================================

export const CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX = '/cattle/batches/forecast/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

/**
 * Admit exactly one raw decoded-safe suffix that CattleForecastBatchPage
 * itself can consume. Decode before final validation to reject encoded
 * separators, controls, dot segments, and malformed escape sequences; then
 * require the raw and decoded values to be identical so the page sees the
 * same key the capability audited. Month semantics are deliberately owned by
 * the page, which has a dedicated invalid-month state.
 */
export function isCanonicalCattleForecastBatchDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }

  const rawMonthKey = pathname.slice(CATTLE_FORECAST_BATCH_DETAIL_PATHNAME_PREFIX.length);
  if (
    rawMonthKey === '' ||
    rawMonthKey.includes('/') ||
    rawMonthKey.includes('\\') ||
    rawMonthKey.includes('\0') ||
    rawMonthKey.includes('\r') ||
    rawMonthKey.includes('\n') ||
    rawMonthKey.includes('?') ||
    rawMonthKey.includes('#')
  ) {
    return false;
  }

  try {
    const monthKey = decodeURIComponent(rawMonthKey);
    return (
      monthKey === rawMonthKey &&
      !monthKey.includes('/') &&
      !monthKey.includes('\\') &&
      !monthKey.includes('\0') &&
      !monthKey.includes('\r') &&
      !monthKey.includes('\n') &&
      !monthKey.includes('?') &&
      !monthKey.includes('#') &&
      monthKey.trim() !== '' &&
      monthKey !== '.' &&
      monthKey !== '..'
    );
  } catch {
    return false;
  }
}

/**
 * React Router supplies pathname/search/hash separately. A plain string is a
 * pathname-only convenience for focused unit seams; it cannot smuggle a
 * query or fragment into the direct release.
 */
export function isCanonicalCattleForecastBatchDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalCattleForecastBatchDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '') &&
    isCanonicalCattleForecastBatchDetailPathname(pathname)
  );
}
